import { FC } from 'react';
import { styled, Typography } from '@mui/material';
import IncrementalLaunchOffsetControl from './IncrementalLaunchOffsetControl';
import IncrementalLaunchReleaseTimeControl from './IncrementalLaunchReleaseTimeControl';

export type IncrementalLaunchTimingValue = {
  offsetUnit: string;
  offsetValue: number;
  releaseTime: Date | null;
  timezone: string;
};

export type IncrementalLaunchTimingSectionProps = {
  baseReleaseTime: Date | null;
  offsetOptions: string[];
  onChange: (value: IncrementalLaunchTimingValue) => void;
  timezoneOptions: string[];
  value: IncrementalLaunchTimingValue;
};

const IncrementalLaunchTimingSection: FC<IncrementalLaunchTimingSectionProps> = ({
  baseReleaseTime,
  offsetOptions,
  onChange,
  timezoneOptions,
  value
}) => {
  const isHoursSelected = value.offsetUnit === 'Hours';

  const getReleaseTimeForOffset = (nextOffsetUnit: string, nextOffsetValue: number): Date | null => {
    if (!baseReleaseTime) {
      return value.releaseTime;
    }

    if (nextOffsetUnit === 'Hours') {
      return new Date(baseReleaseTime.getTime() + nextOffsetValue * 60 * 60 * 1000);
    }

    return value.releaseTime;
  };

  const handleOffsetValueChange = (offsetValue: number) => {
    onChange({
      ...value,
      offsetValue,
      releaseTime: getReleaseTimeForOffset(value.offsetUnit, offsetValue)
    });
  };

  const handleOffsetUnitChange = (offsetUnit: string) => {
    onChange({
      ...value,
      offsetUnit,
      offsetValue: 0,
      releaseTime: baseReleaseTime
    });
  };

  const handleReleaseTimeChange = (releaseTime: Date | null) => {
    if (isHoursSelected) {
      return;
    }

    onChange({
      ...value,
      releaseTime
    });
  };

  const handleTimezoneChange = (timezone: string) => {
    onChange({
      ...value,
      timezone
    });
  };

  return (
    <Container>
      <IncrementalLaunchOffsetControl
        onOptionChange={handleOffsetUnitChange}
        onValueChange={handleOffsetValueChange}
        options={offsetOptions}
        selectedOption={value.offsetUnit}
        value={value.offsetValue}
      />
      <Typography variant="body1">at</Typography>
      <IncrementalLaunchReleaseTimeControl
        disabled={isHoursSelected}
        onTimeChange={handleReleaseTimeChange}
        onTimezoneChange={handleTimezoneChange}
        timezone={value.timezone}
        timezoneOptions={timezoneOptions}
        value={value.releaseTime}
      />
    </Container>
  );
};

export default IncrementalLaunchTimingSection;

const Container = styled('div')`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;
